import { Logger, TLogger } from '@streaming/mouflon-utils';
import { ICustomParser, IManifest, Parser } from 'm3u8-parser';

import { TEventBus } from '@eventBus/EventBus';
import { IRawPlaylist } from '@streamLoader/BasePlaylistRotationController/IBasePlaylistRotationController';
import { IPlaylistCorruptionInfo } from '@streamLoader/PlaylistDecrypt/IPlaylistDecryptStage';
import { PlaylistParserDecryptStage } from '@streamLoader/PlaylistDecrypt/PlaylistParsers/PlaylistParserDecryptStage';
import { PlaylistValidatorDecryptStage } from '@streamLoader/PlaylistDecrypt/PlaylistValidators/PlaylistValidatorDecryptStage';
import { SCHEME_MAP } from '@streamLoader/PlaylistDecrypt/RestoreSchemes';
import { KEYS_MAP, VALIDATORS_MAP } from '@streamLoader/PlaylistDecrypt/ValidatorSchema';
import { extractEncodeTimestampFromExtraData } from '@streamLoader/helpers/frameExternalnfo';


export class PlaylistDecryptHandler {
  public static SCHEME_QUERY_PARAMETER_NAME: string = 'psch';
  public static KEY_QUERY_PARAMETER_NAME: string = 'pkey';
  public static CORRUPTION_SCHEME_PARSER: ICustomParser = {
    expression: /^#EXT-X-MOUFLON:PSCH(.*)/,
    customType: 'mmpCorruptionScheme',
    dataParser: (str: string) => {
      const content: string = str.replace(/^#EXT-X-MOUFLON:PSCH:/, '').trim();
      const parts: string[] = content.split(':');

      return parts;
    },
  };

  private _activeScheme: string | null = null;
  private _activeKey: string | null = null;

  private readonly _knownKeys: Record<string, string> = KEYS_MAP;
  private readonly _knownKeyIds: string[] = Object.keys(this._knownKeys);
  private readonly _knownSchemes: Record<string, PlaylistParserDecryptStage[]> = SCHEME_MAP;
  private readonly _knownSchemesVersions: string[] = Object.keys(this._knownSchemes);
  private readonly _validatorsMap: Record<string, PlaylistValidatorDecryptStage[]> = VALIDATORS_MAP;
  private readonly _logger: TLogger;

  constructor(eventBus: TEventBus) {
    this._logger = Logger(eventBus, 'PlaylistDecryptHandler');
  }

  public reset(): void {
    this._logger.info('Reset');
    this._activeKey = null;
    this._activeScheme = null;
  }

  public handleAvailableSchemes(schemes: IPlaylistCorruptionInfo[]): void {
    this._logger.info('Available schemes reported', schemes);
    const selectedScheme: IPlaylistCorruptionInfo | undefined = schemes.find(
      ({ schemeVersion, keyId }) => this._knownKeyIds.includes(keyId) && this._knownSchemesVersions.includes(schemeVersion),
    );

    if (selectedScheme) {
      this._logger.info('Selected scheme', selectedScheme);
      this._activeScheme = selectedScheme.schemeVersion;
      this._activeKey = selectedScheme.keyId;
    } else {
      this._logger.info('No selected scheme found', selectedScheme);
      this.reset();
    }
  }

  public handlePlaylist(rawPlaylist: IRawPlaylist): IManifest {
    if (!this.hasCorruptionInfo || !rawPlaylist.isCorruptRequested) {
      return this._handleRawPlaylist(rawPlaylist.data);
    }

    const validators: PlaylistValidatorDecryptStage[] = this._activeScheme ? this._validatorsMap[this._activeScheme] : [];

    if (
      validators
        .filter(({ pipelinePosition }) => pipelinePosition === 'preParse')
        .some((validator) => !validator.validateScheme(rawPlaylist.data, this._activeScheme, this._activeKey))
    ) {
      return this._handleRawPlaylist(rawPlaylist.data);
    }

    const decryptedRawPlaylist: string = this._activeScheme
      ? this._knownSchemes[this._activeScheme!]
          .filter(({ pipelinePosition }) => pipelinePosition === 'preParse')
          .reduce<string>((playlist: string, decryptor: PlaylistParserDecryptStage) => {
            return decryptor.restorePlaylist(playlist, this._knownKeys[this._activeKey!]) as string;
          }, rawPlaylist.data)
      : rawPlaylist.data;

    const parser: Parser = new Parser();

    parser.addParser(PlaylistDecryptHandler.CORRUPTION_SCHEME_PARSER);
    parser.addParser({
      expression: /^#EXT-X-MOUFLON:EXT-REF/,
      customType: 'encodeTimestampMap',
      dataParser: function (line: string) {
        return extractEncodeTimestampFromExtraData(line.replace('#EXT-X-MOUFLON:EXT-REF:', ''));
      },
    });
    parser.push(decryptedRawPlaylist);
    parser.end();

    const { manifest } = parser;

    if (
      validators
        .filter(({ pipelinePosition }) => pipelinePosition === 'postParse')
        .some((validator) => !validator.validateScheme(manifest, this._activeScheme, this._activeKey))
    ) {
      return manifest;
    }

    return this._knownSchemes[this._activeScheme!]
      .filter(({ pipelinePosition }) => pipelinePosition === 'postParse')
      .reduce<IManifest>((playlist, decryptor) => {
        return decryptor.restorePlaylist(playlist, this._knownKeys[this._activeKey!]) as IManifest;
      }, manifest);
  }

  private _handleRawPlaylist(rawPlaylist: string): IManifest {
    const parser: Parser = new Parser();

    const rawCorruptionInfo: IterableIterator<RegExpMatchArray> = rawPlaylist.matchAll(/^#EXT-X-MOUFLON:PSCH:([^:\r\n]+):([^\r\n]+)/gm);

    const mmpCorruptionScheme: IPlaylistCorruptionInfo[] = Array.from(rawCorruptionInfo, (match) => ({
      schemeVersion: match[1],
      keyId: match[2],
    }));

    parser.addParser(PlaylistDecryptHandler.CORRUPTION_SCHEME_PARSER);
    parser.addParser({
      expression: /^#EXT-X-MOUFLON:EXT-REF/,
      customType: 'encodeTimestampMap',
      dataParser: function (line: string) {
        return extractEncodeTimestampFromExtraData(line.replace('#EXT-X-MOUFLON:EXT-REF:', ''));
      },
    });
    parser.push(rawPlaylist);
    parser.end();

    if (mmpCorruptionScheme) {
      this.handleAvailableSchemes(mmpCorruptionScheme);
    }

    return parser.manifest;
  }

  get hasCorruptionInfo(): boolean {
    return !!this._activeScheme && !!this._activeKey;
  }

  get corruptionQueryParams(): URLSearchParams {
    const searchParams: URLSearchParams = new URLSearchParams();

    if (this.hasCorruptionInfo) {
      searchParams.set(PlaylistDecryptHandler.SCHEME_QUERY_PARAMETER_NAME, this._activeScheme!);
      searchParams.set(PlaylistDecryptHandler.KEY_QUERY_PARAMETER_NAME, this._activeKey!);
    }

    return searchParams;
  }
}
